/**
 * Cross-Thread Search service (PR fix/thread-isolation-regression-and-cross-thread-retrieval).
 *
 * Miles directive (2026-05-09):
 *
 *   "Each thread needs to be separate in conversation history to keep
 *    projects separate but Prest0n can pull info from different current
 *    or old archived threads of course like you do in this Slack thread."
 *
 * Per-thread isolation (PR #72) prevents cross-talk between in-flight
 * sends, but it must NOT prevent recall. When a user in thread C asks
 * "what was the answer on the workers' comp question I asked last week?",
 * Prest0n should be able to surface the relevant snippet from thread A
 * (current OR archived) and cite it inline.
 *
 * Design principles:
 *
 *  1. Pure function, no React. Easy to unit-test without mounting Firebase
 *     or the chat system.
 *  2. Operates on the same per-thread localStorage keys + archive that
 *     `useChatThreads` writes to (`getThreadStorageKeys`). No new schema.
 *  3. Lightweight keyword scoring (tokenize → IDF-weighted overlap). No
 *     embedding service required \u2014 the corpus is small (the user's own
 *     thread history) and demo speed matters.
 *  4. Caps:
 *       \u2022 max 5 results
 *       \u2022 each snippet capped at 240 chars
 *       \u2022 total search universe truncated to most recent 50 threads
 *         (current + archived) so a power user doesn't blow past
 *         localStorage read budgets.
 *  5. Returns a stable shape the LLM can format inline:
 *       [{ threadId, threadName, snippet, timestamp, archived }]
 *
 * The literal name `crossThreadSearch` is the live-bundle verification
 * grep marker for this fix.
 */

import type {
  ArchivedThread,
  ChatMessage,
  ChatThread,
} from '../../components/GlobalChatSystem/types';
import { getAuth } from 'firebase/auth';
import { getThreadStorageKeys } from '../../components/GlobalChatSystem/types';
import { getFromLocalStorage } from '../../components/GlobalChatSystem/utils';
import { tokenize, isStopword, scoreDocument, extractSnippet } from './textCore';

/**
 * Tiny dependency-free HTML-to-text shim. The host's `htmlToPlainText`
 * uses DOMParser which doesn't work outside the browser; this service
 * runs both in the browser and in the Jest unit tests so we use a
 * regex strip instead. It's deliberately conservative — we only need
 * the rough text to score and snippet against, not faithful rendering.
 */
function stripHtml(html: string): string {
  if (!html) return '';
  return html
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<\/(?:p|div|li|h[1-6]|br|tr)>/gi, '\n')
    .replace(/<br\s*\/?\s*>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

// ────────────────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────────────────

export interface CrossThreadSearchResult {
  /** Stable thread id; the chat UI can switch to this thread on click. */
  threadId: string;
  /** Human-readable thread title (the chip label). */
  threadName: string;
  /** Up to 240 chars of the matching message text, with ellipsis. */
  snippet: string;
  /** ISO timestamp of the matching message (if known) or thread last-active. */
  timestamp: string;
  /** True when the source thread is in the archive, false for current threads. */
  archived: boolean;
  /** The matching message's role (user vs. assistant). Useful for citation phrasing. */
  isUser: boolean;
  /** Internal score; higher = better match. Exposed for tests + tie-breaking. */
  score: number;
}

export interface CrossThreadSearchOptions {
  /**
   * Required \u2014 same instanceId useChatThreads uses (e.g. 'global_chat',
   * 'fernando_hidalgo_chat'). The service reads localStorage keyed on
   * this prefix so it cannot accidentally leak another tenant's data.
   */
  instanceId: string;
  /** The free-text query to score against. */
  query: string;
  /** Optional list of thread ids to restrict the search to. */
  threadFilter?: string[];
  /** When true, search ONLY archived threads (default false = both). */
  archivedOnly?: boolean;
  /** Cap on results returned. Defaults to 5. */
  maxResults?: number;
  /** Cap on snippet length. Defaults to 240. */
  maxSnippetChars?: number;
  /** Optional auth uid to scope storage keys. */
  uid?: string | null;
  /**
   * Localstorage adapter for tests. When omitted, uses
   * `getFromLocalStorage`. The shape mirrors what useChatThreads writes.
   */
  localStorageAdapter?: {
    readThreads: (key: string) => ChatThread[];
    readArchive: (key: string) => ArchivedThread[];
    readMessages: (key: string) => ChatMessage[];
  };
  /**
   * If supplied, exclude this thread id from results \u2014 typically the
   * thread the user is currently typing in, since they'd never want
   * their own current thread cited back at them.
   */
  excludeThreadId?: string | null;
}

// ────────────────────────────────────────────────────────────────────────────
// Tokenization & Scoring
// ────────────────────────────────────────────────────────────────────────────

// The pure text core lives in ./textCore (2026-09-09); re-exported here so
// every existing import path keeps working.
export { tokenize, isStopword, scoreDocument, extractSnippet } from './textCore';

// ────────────────────────────────────────────────────────────────────────────
// Main entry point
// ────────────────────────────────────────────────────────────────────────────

const DEFAULT_MAX_RESULTS = 5;
const DEFAULT_MAX_SNIPPET = 240;
/** Hard cap on threads scanned per call (current + archived combined). */
const MAX_THREADS_SCANNED = 50;

/**
 * Default localStorage adapter \u2014 reads JSON from the live browser
 * localStorage via the same util useChatThreads uses.
 */
function defaultAdapter() {
  return {
    readThreads: (key: string): ChatThread[] => {
      const v = getFromLocalStorage<ChatThread[]>(key, [] as ChatThread[]);
      return Array.isArray(v) ? v : [];
    },
    readArchive: (key: string): ArchivedThread[] => {
      const v = getFromLocalStorage<ArchivedThread[]>(key, [] as ArchivedThread[]);
      return Array.isArray(v) ? v : [];
    },
    readMessages: (key: string): ChatMessage[] => {
      const v = getFromLocalStorage<ChatMessage[]>(key, [] as ChatMessage[]);
      return Array.isArray(v) ? v : [];
    },
  };
}

/**
 * Search across the user's current and archived threads for messages
 * relevant to `query`. Returns up to `maxResults` matches sorted by
 * descending relevance.
 *
 * The implementation is deliberately small + dependency-free so it can
 * be invoked synchronously from the orchestrator's tool-dispatch loop
 * without await overhead.
 */
export function crossThreadSearch(opts: CrossThreadSearchOptions): CrossThreadSearchResult[] {
  const {
    instanceId,
    query,
    threadFilter,
    archivedOnly = false,
    maxResults = DEFAULT_MAX_RESULTS,
    maxSnippetChars = DEFAULT_MAX_SNIPPET,
    excludeThreadId = null,
  } = opts;
  const adapter = opts.localStorageAdapter || defaultAdapter();

  if (!instanceId || !query || !query.trim()) return [];

  const queryTokensRaw = tokenize(query);
  const queryTokens = queryTokensRaw.filter(t => !isStopword(t));
  if (queryTokens.length === 0) return [];

  let resolvedUid: string | null = typeof opts.uid === 'string' ? opts.uid : null;
  if (resolvedUid == null) {
    try {
      resolvedUid = getAuth().currentUser?.uid || null;
    } catch {
      resolvedUid = null;
    }
  }
  const keys = getThreadStorageKeys(instanceId, resolvedUid);

  // Build the search universe: list of { threadMeta, archived, messages }
  type Universe = Array<{
    thread: ChatThread;
    archived: boolean;
    messages: ChatMessage[];
  }>;
  const universe: Universe = [];

  if (!archivedOnly) {
    const threads = adapter.readThreads(keys.THREAD_LIST);
    for (const t of threads) {
      if (threadFilter && !threadFilter.includes(t.id)) continue;
      if (excludeThreadId && t.id === excludeThreadId) continue;
      const msgs = adapter.readMessages(keys.THREAD_MSG_PREFIX + t.id);
      universe.push({ thread: t, archived: false, messages: msgs });
    }
  }

  const archive = adapter.readArchive(keys.THREAD_ARCHIVE);
  for (const a of archive) {
    if (threadFilter && !threadFilter.includes(a.thread.id)) continue;
    if (excludeThreadId && a.thread.id === excludeThreadId) continue;
    universe.push({ thread: a.thread, archived: true, messages: a.messages || [] });
  }

  // Recency-sort the universe and clamp to MAX_THREADS_SCANNED. Recency
  // is by lastActiveAt (current threads) or archivedAt fallback (archived).
  universe.sort((a, b) => {
    const ta = a.thread.lastActiveAt || '';
    const tb = b.thread.lastActiveAt || '';
    return tb.localeCompare(ta);
  });
  if (universe.length > MAX_THREADS_SCANNED) {
    universe.length = MAX_THREADS_SCANNED;
  }

  // Build a corpus-level document-frequency map across every message in
  // the scanned universe. A "document" here is a single message.
  const documentFrequencies = new Map<string, number>();
  type IndexedDoc = {
    threadId: string;
    threadName: string;
    archived: boolean;
    timestamp: string;
    isUser: boolean;
    text: string;
    tokens: string[];
  };
  const docs: IndexedDoc[] = [];

  for (const entry of universe) {
    for (const msg of entry.messages) {
      const text = (msg.text && msg.text.trim().length > 0)
        ? msg.text
        : (msg.html ? stripHtml(msg.html) : '');
      if (!text || text.trim().length < 4) continue;
      const tokens = tokenize(text).filter(t => !isStopword(t));
      if (tokens.length === 0) continue;

      const tokenSet = new Set(tokens);
      for (const tok of tokenSet) {
        documentFrequencies.set(tok, (documentFrequencies.get(tok) || 0) + 1);
      }

      const ts = msg.timestamp instanceof Date
        ? msg.timestamp.toISOString()
        : (typeof msg.timestamp === 'string' ? msg.timestamp : entry.thread.lastActiveAt);

      docs.push({
        threadId: entry.thread.id,
        threadName: entry.thread.title || 'Untitled thread',
        archived: entry.archived,
        timestamp: ts,
        isUser: !!msg.isUser,
        text,
        tokens,
      });
    }
  }

  // Score every doc.
  const scored: Array<CrossThreadSearchResult> = [];
  for (const d of docs) {
    const s = scoreDocument(queryTokens, d.tokens, documentFrequencies);
    if (s <= 0) continue;
    scored.push({
      threadId: d.threadId,
      threadName: d.threadName,
      snippet: extractSnippet(d.text, queryTokens, maxSnippetChars),
      timestamp: d.timestamp,
      archived: d.archived,
      isUser: d.isUser,
      score: s,
    });
  }

  scored.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    // Tie-break on recency.
    return (b.timestamp || '').localeCompare(a.timestamp || '');
  });

  // De-dup by threadId so a single thread doesn't dominate the result
  // set (one snippet per thread is plenty for citation purposes).
  const seenThreads = new Set<string>();
  const final: CrossThreadSearchResult[] = [];
  for (const r of scored) {
    if (seenThreads.has(r.threadId)) continue;
    seenThreads.add(r.threadId);
    final.push(r);
    if (final.length >= maxResults) break;
  }
  return final;
}

// ────────────────────────────────────────────────────────────────────────────
// Cross-thread intent detection
// ────────────────────────────────────────────────────────────────────────────

/**
 * Lightweight heuristic to detect when the user is asking about something
 * from a previous thread. Used by the orchestrator to decide whether to
 * fan out a `crossThreadSearch` call before answering.
 *
 * False positives are cheap (we just call the search and return zero
 * results); false negatives are the costly case (the user feels we
 * "forgot" something we should have remembered). So this leans
 * permissive.
 */
export function detectsCrossThreadIntent(text: string): boolean {
  if (!text) return false;
  const lower = text.toLowerCase();
  const patterns = [
    // English
    /\bthe (?:[a-z\- ]+ )?thread\b/i,
    /\bin (?:the )?(?:previous|earlier|other|last|prior) (?:thread|chat|conversation|project)\b/i,
    /\b(?:last|earlier|previous|prior) (?:week|time|session|conversation|chat)\b/i,
    /\bremember when\b/i,
    /\bdid we (?:talk|discuss|cover|go over)\b/i,
    /\bwhat (?:did|was) (?:we|i) (?:say|ask|discuss|cover)\b/i,
    /\bwe (?:already )?(?:talked|discussed|covered) about\b/i,
    /\b(?:before|earlier) (?:i|we) (?:asked|said|talked)\b/i,
    /\bgo back to\b/i,
    /\bthe (?:[a-z\- ]+ )?(?:research|case|matter|project|file|matter) (?:thread|chat|i|we)\b/i,
    // Spanish
    /\bel hilo (?:de|del|sobre|anterior|previo|pasado)\b/i,
    /\b(?:la|el) (?:conversaci[oó]n|chat|hilo|proyecto) (?:anterior|previa|previo|pasada|pasado)\b/i,
    /\b(?:antes|anteriormente) (?:hablamos|hablé|preguntamos|pregunté|discutimos)\b/i,
    /\brecuerdas? cuando\b/i,
    /\b(?:la semana pasada|el otro d[ií]a|la vez pasada|el d[ií]a anterior)\b/i,
    /\b¿?qu[eé] (?:dij(?:iste|e|imos|eron)|preguntamos|pregunt[eé]|hablamos|hablé)\b/i,
  ];
  for (const re of patterns) {
    if (re.test(lower)) return true;
  }
  return false;
}
